import React, { useMemo, useState } from 'react';
import { FaDownload, FaRedo, FaTint } from 'react-icons/fa';
import SEO from '../components/SEO';
import { generateGenericPDF } from '../utils/pdfGenerator';

const formatNum = (value, digits = 2) =>
  Number(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

const WaterStorageCalculator = () => {
  const [tankShape, setTankShape] = useState('rect');
  const [tankLength, setTankLength] = useState(2);
  const [tankWidth, setTankWidth] = useState(1.5);
  const [tankWaterHeight, setTankWaterHeight] = useState(1.2);
  const [tankDiameter, setTankDiameter] = useState(1.5);
  const [fillPercent, setFillPercent] = useState(100);

  const storage = useMemo(() => {
    const fillRatio = Math.min(100, Math.max(0, Number(fillPercent) || 0)) / 100;
    const h = Number(tankWaterHeight) || 0;
    let volumeM3 = 0;

    if (tankShape === 'round') {
      const d = Number(tankDiameter) || 0;
      const r = d / 2;
      volumeM3 = Math.PI * r * r * h;
    } else {
      const l = Number(tankLength) || 0;
      const w = Number(tankWidth) || 0;
      volumeM3 = l * w * h;
    }

    const effectiveVolumeM3 = volumeM3 * fillRatio;
    const liters = effectiveVolumeM3 * 1000;
    const cubicFeet = effectiveVolumeM3 * 35.3147;
    const gallonsUS = liters * 0.264172;
    const tanker5000 = liters / 5000;

    return {
      volumeM3,
      effectiveVolumeM3,
      liters,
      cubicFeet,
      gallonsUS,
      tanker5000,
    };
  }, [tankShape, tankLength, tankWidth, tankWaterHeight, tankDiameter, fillPercent]);

  const resetDefaults = () => {
    setTankShape('rect');
    setTankLength(2);
    setTankWidth(1.5);
    setTankWaterHeight(1.2);
    setTankDiameter(1.5);
    setFillPercent(100);
  };

  const handleDownloadPDF = () => {
    const dimensions = tankShape === 'round'
      ? `Diameter: ${formatNum(tankDiameter)} m, Height: ${formatNum(tankWaterHeight)} m`
      : `Length: ${formatNum(tankLength)} m, Width: ${formatNum(tankWidth)} m, Height: ${formatNum(tankWaterHeight)} m`;

    generateGenericPDF('Water Storage Calculator', {
      'Tank Shape': tankShape === 'round' ? 'Round Tank' : 'Rectangle Tank',
      Dimensions: dimensions,
      'Fill Level': `${formatNum(fillPercent)}%`,
      'Tank Volume (100% Fill)': `${formatNum(storage.volumeM3, 3)} m3`,
      'Effective Volume': `${formatNum(storage.effectiveVolumeM3, 3)} m3`,
      'Water Storage': `${formatNum(storage.liters)} liters`,
      'Volume in Cubic Feet': `${formatNum(storage.cubicFeet)} ft3`,
      'Volume in US Gallons': `${formatNum(storage.gallonsUS)} gal`,
      '5000L Tanker Equivalent': `${formatNum(storage.tanker5000, 2)} tanker(s)`,
    });
  };

  return (
    <div className="calculator-page fade-in">
      <SEO
        title="Water Storage Calculator"
        description="Calculate water tank storage in liters using rectangular or round tank dimensions and water height."
        keywords="water storage calculator, tank capacity calculator, water tank liter calculator, round tank capacity"
        canonicalPath="/water-storage"
        calculatorType="Water Storage Calculator"
        calculatorDescription="Calculate how much water a tank can store based on shape, dimensions, water height, and fill level."
        faqItems={[
          {
            question: 'How is water storage calculated for a rectangular tank?',
            answer: 'Rectangular tank storage is calculated as length x width x water height, then converted from cubic meter to liters.',
          },
          {
            question: 'How is water storage calculated for a round tank?',
            answer: 'Round tank storage uses pi x radius x radius x water height, then converts volume into liters.',
          },
          {
            question: 'Can I calculate partial fill level?',
            answer: 'Yes. You can set fill level percentage to estimate storage for a partially filled tank.',
          },
          {
            question: 'Is the result exact for every tank type?',
            answer: 'It is a geometric estimate based on entered dimensions. Internal structure, slope, and fittings can slightly change actual capacity.',
          },
        ]}
      />

      <div className="calculator-header">
        <h1><FaTint style={{ color: '#0891b2' }} /> Water Storage Calculator</h1>
        <p>Calculate water storage by tank dimensions for rectangle or round shape tanks.</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Tank Type</label>
            <div className="tabs">
              <button className={`tab ${tankShape === 'rect' ? 'active' : ''}`} onClick={() => setTankShape('rect')}>
                Rectangle Tank
              </button>
              <button className={`tab ${tankShape === 'round' ? 'active' : ''}`} onClick={() => setTankShape('round')}>
                Round Tank
              </button>
            </div>
          </div>

          {tankShape === 'rect' ? (
            <div className="grid-3-cols">
              <div className="input-group">
                <label>Length (meter)</label>
                <input type="number" min="0" step="0.01" value={tankLength} onChange={(e) => setTankLength(e.target.value === '' ? '' : Number(e.target.value))} />
              </div>
              <div className="input-group">
                <label>Width (meter)</label>
                <input type="number" min="0" step="0.01" value={tankWidth} onChange={(e) => setTankWidth(e.target.value === '' ? '' : Number(e.target.value))} />
              </div>
              <div className="input-group">
                <label>Water Height (meter)</label>
                <input type="number" min="0" step="0.01" value={tankWaterHeight} onChange={(e) => setTankWaterHeight(e.target.value === '' ? '' : Number(e.target.value))} />
              </div>
            </div>
          ) : (
            <div className="grid-3-cols">
              <div className="input-group">
                <label>Diameter (meter)</label>
                <input type="number" min="0" step="0.01" value={tankDiameter} onChange={(e) => setTankDiameter(e.target.value === '' ? '' : Number(e.target.value))} />
              </div>
              <div className="input-group">
                <label>Water Height (meter)</label>
                <input type="number" min="0" step="0.01" value={tankWaterHeight} onChange={(e) => setTankWaterHeight(e.target.value === '' ? '' : Number(e.target.value))} />
              </div>
              <div className="input-group">
                <label>Fill Level (%)</label>
                <input type="number" min="0" max="100" step="1" value={fillPercent} onChange={(e) => setFillPercent(e.target.value === '' ? '' : Number(e.target.value))} />
              </div>
            </div>
          )}

          {tankShape === 'rect' && (
            <div className="input-group" style={{ maxWidth: '260px' }}>
              <label>Fill Level (%)</label>
              <input type="number" min="0" max="100" step="1" value={fillPercent} onChange={(e) => setFillPercent(e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
          )}

          <div className="button-group">
            <button className="btn btn-outline" onClick={resetDefaults}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          <div className="result-summary" style={{ background: 'linear-gradient(135deg, #0891b2, #0e7490)' }}>
            <h3>Water Storage</h3>
            <div className="amount">{formatNum(storage.liters)} liters</div>
            <small>{formatNum(storage.effectiveVolumeM3, 3)} m3 effective volume</small>
          </div>

          <div className="result-details">
            <div className="result-item">
              <span className="label">Tank Volume (100% fill)</span>
              <span className="value">{formatNum(storage.volumeM3, 3)} m3</span>
            </div>
            <div className="result-item">
              <span className="label">Effective Volume</span>
              <span className="value">{formatNum(storage.effectiveVolumeM3, 3)} m3</span>
            </div>
            <div className="result-item" style={{ background: '#ecfeff' }}>
              <span className="label">Water (Liters)</span>
              <span className="value">{formatNum(storage.liters)} L</span>
            </div>
            <div className="result-item">
              <span className="label">Volume in Cubic Feet</span>
              <span className="value">{formatNum(storage.cubicFeet)} ft3</span>
            </div>
            <div className="result-item">
              <span className="label">US Gallons</span>
              <span className="value">{formatNum(storage.gallonsUS)} gal</span>
            </div>
            <div className="result-item">
              <span className="label">5000L Tanker Equivalent</span>
              <span className="value">{formatNum(storage.tanker5000, 2)} tanker(s)</span>
            </div>
          </div>
        </div>
      </div>

      <div className="info-box" style={{ marginTop: '24px' }}>
        <h4>Notes</h4>
        <ul>
          <li>Use internal dimensions for better accuracy.</li>
          <li>Water height means current filled height, not total tank wall height.</li>
          <li>For sloped or irregular tanks, treat this as an estimate.</li>
        </ul>
      </div>
    </div>
  );
};

export default WaterStorageCalculator;
